import { Button } from '@evaneos/b2b-ui/legacy';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import { ERROR, PENDING, SUCCESS } from '../../../../design-system/constants';
import { Close } from '../../../../design-system/Icons/Close';
import { Search } from '../../../../design-system/Icons/Search';
import { Loader } from '../../../../design-system/Loader';
import { DossierId } from '../../../../resources/dossier/types';
import {
    getAllMessageTemplates,
    getAllMessageTemplatesRequest,
    getMessageTemplateSearchIndex,
    getMessageTemplateStatus,
} from '../../../../resources/message-template';
import { MessageTemplate } from '../../../../resources/message-template/types';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';
import { useAppDispatch, useAppSelector } from '../../../../shared/redux';
import { search } from '../../../../shared/utilities/search';
import { NoMessageTemplates } from '../../../MessageTemplate/components/NoMessageTemplates';

import { ErrorOverlay } from '../../../Pipeline/components/Error';

import s from './MessageTemplatesPanel.css';

type Props = {
    dossierId: DossierId;
    handleMessageTemplatesPanel: () => void;
    displayPanel: boolean;
    applyMessageTemplate: (messageTemplate: MessageTemplate) => void;
};

export const MessageTemplatesPanel = ({
    dossierId,
    handleMessageTemplatesPanel,
    displayPanel,
    applyMessageTemplate,
}: Props): JSX.Element => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const { trackEvent } = useTrackEventAmplitude();
    const status = useAppSelector(getMessageTemplateStatus);
    const messageTemplates = useAppSelector(getAllMessageTemplates);
    const messageTemplateSearchIndex = useAppSelector(getMessageTemplateSearchIndex);
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const nodeRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        dispatch(getAllMessageTemplatesRequest());
    }, [dispatch]);
    const [searchResultIds, setSearchResultIds] = useState<string[]>([]);
    const [searchQuery, handleSearchQuery] = useState('');
    const selectMessageTemplate = (messageTemplate: MessageTemplate) => {
        applyMessageTemplate(messageTemplate);
        handleMessageTemplatesPanel();
        trackEvent((ampli, defaultProperties) => {
            ampli.discussionTemplateSelected({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
    };

    const handleSearch = (e: React.FormEvent<HTMLInputElement>): void => {
        handleSearchQuery(e.currentTarget.value);
        const results = search(e.currentTarget.value, messageTemplateSearchIndex);
        setSearchResultIds(results);
    };

    const filteredMessageTemplates: MessageTemplate[] =
        searchQuery.length >= 2
            ? searchResultIds.map((id) => messageTemplates[id])
            : Object.values(messageTemplates);

    const displayNoMessageTemplate =
        status === SUCCESS && searchQuery === '' && filteredMessageTemplates.length === 0;

    return (
        <CSSTransition
            in={displayPanel}
            timeout={{ appear: 0, enter: 0, exit: 200 }}
            classNames={{
                enterDone: s.enterDone,
                exit: s.exit,
            }}
            mountOnEnter
            unmountOnExit
            nodeRef={nodeRef}
        >
            <div ref={nodeRef} className={s.container}>
                <div className={s.top}>
                    <h1>Templates</h1>
                    {status !== ERROR && (
                        <React.Fragment>
                            <div className={s.searchInput}>
                                <Search className={s.searchIcon} color="var(--ink-light)" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={handleSearch}
                                    placeholder={formatMessage({
                                        id: 'dossier.search_template',
                                    })}
                                    data-testid="message-template-search-input"
                                />
                            </div>
                            {!displayNoMessageTemplate && (
                                <Link
                                    to={{ pathname: '/templates/new' }}
                                    data-testid="message-templates-new"
                                >
                                    <Button>
                                        <FormattedMessage id="templates.bo.new" />
                                    </Button>
                                </Link>
                            )}
                        </React.Fragment>
                    )}
                </div>
                <button type="button" className={s.close} onClick={handleMessageTemplatesPanel}>
                    <Close color="var(--ink)" />
                </button>
                <div className={s.content}>
                    {status === PENDING && (
                        <div className={s.loaderContainer}>
                            <Loader className={s.loader} />
                        </div>
                    )}
                    {status === SUCCESS && (
                        <React.Fragment>
                            {filteredMessageTemplates.map((messageTemplate: MessageTemplate) => (
                                <div
                                    key={messageTemplate.id}
                                    className={s.messageTemplate}
                                    data-testid={`message-template-${messageTemplate.id}`}
                                    onClick={() => selectMessageTemplate(messageTemplate)}
                                >
                                    {messageTemplate.title}
                                </div>
                            ))}
                        </React.Fragment>
                    )}
                    {displayNoMessageTemplate && (
                        <div className={s.noMessageTemplate}>
                            <NoMessageTemplates />
                        </div>
                    )}
                    {status === ERROR && (
                        <ErrorOverlay
                            onRefresh={() => dispatch(getAllMessageTemplatesRequest())}
                            className={s.error}
                            data-testid="message-template-error"
                        />
                    )}
                </div>
            </div>
        </CSSTransition>
    );
};
